import React from 'react';
import { adsConfig, canServePlacement, type AdPlacement } from '../lib/ads-config';
import { loadAdSense, requestAdUnit } from '../lib/ad-loader';
import { useAdPrivacy } from './AdPrivacy';
import '../styles/advertising.css';

interface AdUnitProps { placement: AdPlacement; }

/** One manually placed, non-personalized banner after useful page content. */
export function AdUnit({ placement }: AdUnitProps) {
  const { adsAllowed } = useAdPrivacy();
  const adRef = React.useRef<HTMLModElement>(null);
  const permission = React.useRef(adsAllowed);
  permission.current = adsAllowed;
  const enabled = canServePlacement(placement) && adsAllowed;

  React.useEffect(() => {
    const element = adRef.current;
    if (!enabled || !element) return;
    let active = true;
    const initialize = () => {
      if (!active || !permission.current || !element.isConnected || document.visibilityState === 'hidden') return;
      void loadAdSense().then(() => {
        if (active && permission.current && element.isConnected && document.visibilityState !== 'hidden') requestAdUnit(element);
      }, () => undefined);
    };
    let observer: IntersectionObserver | undefined;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) initialize();
      }, { rootMargin: '200px 0px' });
      observer.observe(element);
    } else {
      initialize();
    }
    const onVisibility = () => {
      const bounds = element.getBoundingClientRect();
      if (bounds.top <= window.innerHeight + 200 && bounds.bottom >= -200) initialize();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <aside className="advertising-placement" aria-label="Advertisements" data-ad-placement={placement}>
      <p className="advertising-label">Advertisements</p>
      <div className="advertising-banner">
        <ins
          ref={adRef}
          className="adsbygoogle"
          style={{ display: 'block', minWidth: '0px', maxWidth: '970px', width: '100%', height: '90px' }}
          data-ad-client={adsConfig.publisherId}
          data-ad-slot={adsConfig.slots[placement]}
        />
      </div>
    </aside>
  );
}
